// DuruScan sonic identity — short, clean, technological cues synthesized with the
// WebAudio API (no audio assets, no looping). Respects an app-level Sound Effects
// preference and never plays unless resumed by a user gesture.
const KEY = "dv_sound_fx";
let ctx = null;

export const soundEnabled = () => (typeof localStorage !== "undefined" ? localStorage.getItem(KEY) !== "off" : true);
export const setSoundEnabled = (on) => { try { localStorage.setItem(KEY, on ? "on" : "off"); } catch { /* ignore */ } };

function ac() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

// Unlock audio on a user gesture (browser autoplay policy).
export function primeScanAudio() { try { ac(); } catch { /* ignore */ } }

function blip(c, freq, at, dur, { type = "sine", gain = 0.05, ramp } = {}) {
  const t = c.currentTime + at;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  if (ramp) osc.frequency.linearRampToValueAtTime(ramp, t + dur);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(gain, t + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(g).connect(c.destination);
  osc.start(t);
  osc.stop(t + dur + 0.02);
}

// name: detected | capture | scanning | complete | warning | verify
export function playScanSound(name) {
  if (!soundEnabled()) return;
  const c = ac();
  if (!c) return;
  try {
    switch (name) {
      case "detected": blip(c, 880, 0, 0.09, { type: "sine", gain: 0.025 }); break;
      case "capture": blip(c, 1250, 0, 0.05, { type: "triangle", gain: 0.06 }); blip(c, 1900, 0.045, 0.06, { type: "sine", gain: 0.045 }); break;
      case "scanning": blip(c, 480, 0, 0.28, { type: "sine", gain: 0.02, ramp: 1150 }); break;
      case "complete": blip(c, 880, 0, 0.10, { type: "sine", gain: 0.05 }); blip(c, 1320, 0.11, 0.16, { type: "sine", gain: 0.05 }); break;
      case "warning": blip(c, 200, 0, 0.20, { type: "sine", gain: 0.05 }); break;
      case "verify": blip(c, 660, 0, 0.08, { type: "sine", gain: 0.05 }); blip(c, 990, 0.08, 0.09, { type: "sine", gain: 0.05 }); blip(c, 1320, 0.18, 0.16, { type: "sine", gain: 0.05 }); break;
      default: break;
    }
  } catch { /* ignore */ }
}

// Light haptic where supported (respects nothing that browsers don't expose).
export function haptic(ms = 18) { try { navigator.vibrate?.(ms); } catch { /* ignore */ } }

// Cheap client-side sharpness estimate on a downscaled grayscale copy.
// Returns { sharp: bool, score } — used to warn on obviously blurry captures.
export function estimateSharpness(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const W = 220, H = Math.max(1, Math.round((img.height / img.width) * W));
        const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
        const cx = cv.getContext("2d"); cx.drawImage(img, 0, 0, W, H);
        const d = cx.getImageData(0, 0, W, H).data;
        const gray = new Float32Array(W * H);
        for (let i = 0, j = 0; i < d.length; i += 4, j++) gray[j] = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
        let sum = 0, sum2 = 0, n = 0;
        for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
          const k = y * W + x;
          const lap = 4 * gray[k] - gray[k - 1] - gray[k + 1] - gray[k - W] - gray[k + W];
          sum += lap; sum2 += lap * lap; n++;
        }
        const variance = n ? sum2 / n - (sum / n) ** 2 : 0;
        resolve({ sharp: variance >= 60, score: Math.round(variance) });
      } catch { resolve({ sharp: true, score: 999 }); }
    };
    img.onerror = () => resolve({ sharp: true, score: 999 });
    img.src = dataUrl;
  });
}


// Centralized DuruVault interaction sound identity. Reuses the same WebAudio
// context, the same `dv_sound_fx` preference and the same soft envelope as the
// scan cues above — so there is a single source of truth for all app sounds.
// Usage: DuruSound.play("click" | "open" | "success" | "scan" | "secure" | "twin")
export const DuruSound = {
  isEnabled: soundEnabled,
  setEnabled: setSoundEnabled,
  prime: primeScanAudio,
  play(type) {
    if (!soundEnabled()) return;
    const c = ac();
    if (!c) return;
    try {
      switch (type) {
        // Very short soft digital tactile tick (~50ms).
        case "click": blip(c, 1180, 0, 0.05, { type: "triangle", gain: 0.035, ramp: 820 }); break;
        // Soft "secure document opened" rising pulse.
        case "open": blip(c, 440, 0, 0.16, { type: "sine", gain: 0.05, ramp: 880 }); blip(c, 660, 0.04, 0.14, { type: "sine", gain: 0.03, ramp: 990 }); break;
        // Two-stage digital confirmation (< 300ms).
        case "success": blip(c, 660, 0, 0.10, { type: "sine", gain: 0.05 }); blip(c, 988, 0.10, 0.16, { type: "sine", gain: 0.05 }); break;
        // Futuristic scanner sweep — DuruScan signature.
        case "scan": blip(c, 320, 0, 0.19, { type: "sawtooth", gain: 0.03, ramp: 1900 }); blip(c, 1900, 0.19, 0.08, { type: "sine", gain: 0.02, ramp: 700 }); break;
        // Deeper "sealed / secured" lock tone.
        case "secure": blip(c, 300, 0, 0.14, { type: "sine", gain: 0.055, ramp: 180 }); blip(c, 150, 0.12, 0.10, { type: "triangle", gain: 0.045, ramp: 120 }); break;
        // Soft AI/system awakening shimmer for DuruTwin.
        case "twin": blip(c, 520, 0, 0.22, { type: "sine", gain: 0.04, ramp: 1040 }); blip(c, 526, 0.02, 0.22, { type: "sine", gain: 0.025, ramp: 1052 }); break;
        default: break;
      }
    } catch { /* audio is enhancement-only; never block the action */ }
  },
};
